"use client";

import React from "react";
import Logo from "./Logo";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  ContextMenu,
  ContextMenuTrigger,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
} from "@/components/ui/context-menu";
import { Copy, Download, Type } from "lucide-react";
import { toast } from "sonner";
import {
  copySVGToClipboard,
  downloadBrandAssets,
} from "@/utils/brandAssetUtils";

/**
 * LogoWithContextMenu - Wraps the Logo component with a right-click context menu
 * for brand asset operations (copy SVG, download assets)
 * Hidden on mobile devices for better UX
 */
const LogoWithContextMenu: React.FC = () => {
  const isMobile = useIsMobile();

  /**
   * Handler for copying logo SVG to clipboard
   */
  const handleCopyLogo = async () => {
    try {
      await copySVGToClipboard("/WCD-Brand-Assets/WCD-Logo.svg", "Logo");
      toast.success("Logo SVG copied!");
    } catch (error) {
      toast.error("Failed to copy logo");
    }
  };

  /**
   * Handler for copying wordmark SVG to clipboard
   */
  const handleCopyWordmark = async () => {
    try {
      await copySVGToClipboard(
        "/WCD-Brand-Assets/WCD-Wordmark-dark.svg",
        "Wordmark"
      );
      toast.success("Wordmark copied!");
    } catch (error) {
      toast.error("Failed to copy wordmark");
    }
  };

  /**
   * Handler for downloading brand assets ZIP
   */
  const handleDownloadAssets = () => {
    try {
      downloadBrandAssets();
      toast.success("Downloading brand assets...");
    } catch (error) {
      toast.error("Failed to start download");
    }
  };

  // Don't render context menu on mobile or during SSR (prevents hydration mismatch)
  if (isMobile === undefined || isMobile) {
    return <Logo />;
  }

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>
        <div>
          <Logo />
        </div>
      </ContextMenuTrigger>
      <ContextMenuContent>
        <ContextMenuItem onClick={handleCopyLogo}>
          <Copy className="h-4 w-4 mr-2" />
          Copy logo as SVG
        </ContextMenuItem>
        <ContextMenuItem onClick={handleCopyWordmark}>
          <Type className="h-4 w-4 mr-2" />
          Copy wordmark as SVG
        </ContextMenuItem>
        <ContextMenuSeparator />
        <ContextMenuItem onClick={handleDownloadAssets}>
          <Download className="h-4 w-4 mr-2" />
          Download brand assets
        </ContextMenuItem>
      </ContextMenuContent>
    </ContextMenu>
  );
};

export default LogoWithContextMenu;
